'use client'

import { SIDEBAR_MODULE_CONFIG } from '@/layouts/admin/sidebar-config'
import { NotificationCategory } from '@/lib/api/notifications'
import { useEffect, useRef, useState } from 'react'
import { MdInbox, MdKeyboardArrowDown, MdNotifications } from 'react-icons/md'

export interface CategorySelection {
    module?: string
    resource?: string
}

interface NotificationCategoryMenuProps {
    categories: NotificationCategory[]
    active: CategorySelection
    onSelect: (selection: CategorySelection) => void
    /** Total geral, para a aba "Todas" — vem da lista, não das categorias. */
    totalAll: number
    unreadAll: number
    loading?: boolean
}

/** Contador ao lado do rótulo: não lidas em destaque, total quando tudo lido. */
function CountBadge({ total, unread }: { total: number; unread: number }) {
    if (unread > 0) {
        return (
            <span className="badge badge-secondary badge-sm font-semibold">
                {unread}
            </span>
        )
    }
    return (
        <span className="badge badge-ghost badge-sm text-base-content/50">
            {total}
        </span>
    )
}

/**
 * Menu horizontal de categorias (módulos); as subcategorias (recursos) abrem em
 * dropdown ao clicar.
 *
 * Em cima da lista e não na lateral de propósito: a sidebar já é o menu de
 * navegação do sistema, e repetir aquela estrutura aqui faria o usuário achar
 * que está navegando para os módulos em vez de filtrando a própria caixa.
 *
 * Só chegam categorias que TÊM notificação e que o usuário pode ver — a lista
 * vem pronta do backend, que aplica as mesmas regras de acesso da listagem.
 */
export default function NotificationCategoryMenu({
    categories,
    active,
    onSelect,
    totalAll,
    unreadAll,
    loading,
}: NotificationCategoryMenuProps) {
    // Abre por CLIQUE, não por hover. Com hover o menu piscava no caminho até
    // ele — entre o botão e a lista há um vão, e ao atravessá-lo o ponteiro sai
    // do grupo, fechando e reabrindo — e não havia como fechá-lo ao escolher,
    // porque o ponteiro segue sobre o menu e o `:hover` do CSS continua valendo.
    const [openKey, setOpenKey] = useState<string | null>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    // Fechar ao clicar fora e no Escape — o que se espera de um dropdown, e o
    // que o hover dava de graça.
    useEffect(() => {
        if (!openKey) return

        const onPointerDown = (event: MouseEvent) => {
            if (!menuRef.current?.contains(event.target as Node)) {
                setOpenKey(null)
            }
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpenKey(null)
        }

        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [openKey])

    const handleSelect = (selection: CategorySelection) => {
        setOpenKey(null)
        onSelect(selection)
    }

    if (loading) {
        return (
            <div className="flex items-center gap-2 overflow-hidden">
                {[0, 1, 2, 3].map(i => (
                    <div
                        key={i}
                        className="h-9 w-32 rounded-lg bg-base-300/50 animate-pulse flex-shrink-0"
                    />
                ))}
            </div>
        )
    }

    const isAllActive = !active.module

    return (
        // Quebra linha em vez de rolar na horizontal: `overflow-x-auto` cria
        // contexto de recorte e o dropdown das subcategorias fica invisível,
        // cortado pela própria barra que o contém.
        <div ref={menuRef} className="flex flex-wrap items-center gap-1">
            <button
                type="button"
                className={`btn btn-sm gap-2 flex-shrink-0 ${
                    isAllActive ? 'btn-secondary' : 'btn-ghost'
                }`}
                onClick={() => handleSelect({})}
            >
                <MdNotifications size={16} />
                Todas
                {!isAllActive && (
                    <CountBadge total={totalAll} unread={unreadAll} />
                )}
            </button>

            {categories.map(category => {
                const ModuleIcon =
                    SIDEBAR_MODULE_CONFIG[category.key]?.icon || MdInbox
                const isActive = active.module === category.key
                const hasResources = category.resources.length > 0

                return (
                    <div
                        key={category.key}
                        // Posicionamento próprio, sem as classes `dropdown` do
                        // DaisyUI: elas exibem o menu por `:hover`/`:focus-within`
                        // no CSS, o que ignora o nosso estado — clicar num item
                        // deixava o foco dentro do menu e ele seguia aberto.
                        // Renderizar condicionalmente elimina a disputa.
                        className="relative flex-shrink-0"
                    >
                        <button
                            type="button"
                            aria-haspopup={hasResources ? 'menu' : undefined}
                            aria-expanded={
                                hasResources
                                    ? openKey === category.key
                                    : undefined
                            }
                            className={`btn btn-sm gap-2 ${
                                isActive ? 'btn-secondary' : 'btn-ghost'
                            }`}
                            // Com subcategorias o clique ABRE o menu, e a escolha
                            // é feita lá dentro (inclusive "Tudo em X"). Sem elas
                            // não há menu para abrir: o clique já filtra.
                            onClick={() =>
                                hasResources
                                    ? setOpenKey(
                                          openKey === category.key
                                              ? null
                                              : category.key
                                      )
                                    : handleSelect({ module: category.key })
                            }
                        >
                            <ModuleIcon size={16} />
                            {category.label}
                            <CountBadge
                                total={category.total}
                                unread={category.unread}
                            />
                            {hasResources && (
                                <MdKeyboardArrowDown
                                    size={16}
                                    className="opacity-60"
                                />
                            )}
                        </button>

                        {hasResources && openKey === category.key && (
                            <ul
                                role="menu"
                                className="absolute left-0 top-full z-50 mt-1 menu bg-base-100 rounded-box shadow-lg border border-base-300/60 w-64 p-2"
                            >
                                <li className="menu-title text-xs">
                                    {category.label}
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={
                                            isActive && !active.resource
                                                ? 'active'
                                                : ''
                                        }
                                        onClick={() =>
                                            handleSelect({
                                                module: category.key,
                                            })
                                        }
                                    >
                                        <span className="flex-1 text-left">
                                            Tudo em {category.label}
                                        </span>
                                        <CountBadge
                                            total={category.total}
                                            unread={category.unread}
                                        />
                                    </button>
                                </li>
                                {category.resources.map(resource => (
                                    <li key={resource.key}>
                                        <button
                                            type="button"
                                            className={
                                                active.resource === resource.key &&
                                                isActive
                                                    ? 'active'
                                                    : ''
                                            }
                                            onClick={() =>
                                                handleSelect({
                                                    module: category.key,
                                                    resource: resource.key,
                                                })
                                            }
                                        >
                                            <span className="flex-1 text-left">
                                                {resource.label}
                                            </span>
                                            <CountBadge
                                                total={resource.total}
                                                unread={resource.unread}
                                            />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
